import { del, get, getBlob, post, postForm, put } from '../../api-config';
import { gdpService, getLocale } from './_service';

// Módulo de facturación (FACTURACION_MODULO_SPEC.md). Todas las respuestas JSON son SimpleApiResponse: los errores de
// dominio llegan con success=false y un errorCode (600X para las reglas de la liquidación).

// El nombre del identificador del paciente llega resuelto en el idioma de la sesión.
export const getBillingContracts = () => get(gdpService, '/billing/contracts', null, { locale: getLocale() });

export const postBillingContract = (contract: object) =>
  post(gdpService, '/billing/contracts', contract, { locale: getLocale() });

/** Los tres campos identificadores con el nombre que tienen en ese grupo. */
export const getBillingPatientIdFields = (groupId: string | null) =>
  get(gdpService, '/billing/patient-id-fields', null, { groupId: groupId ?? undefined, locale: getLocale() });

export const getBillingClients = () => get(gdpService, '/billing/clients', null, null);

export const getBillingFormulas = () => get(gdpService, '/billing/formulas', null, null);

export const putBillingContractAutoDraft = (contractId: string, autoDraftDay: number | null) =>
  put(gdpService, `/billing/contracts/${contractId}/auto-draft`, { autoDraftDay });

export const putBillingContractIssueOnValidation = (contractId: string, enabled: boolean) =>
  put(gdpService, `/billing/contracts/${contractId}/issue-on-client-validation`, { enabled });

/** Pacientes del ámbito del contrato (para elegir a quién se le corrige el fin de servicio). */
export const getBillingContractPatients = (contractId: string) =>
  get(gdpService, `/billing/contracts/${contractId}/patients`, null, null);

export const getBillingServiceCorrections = (contractId: string) =>
  get(gdpService, `/billing/contracts/${contractId}/service-corrections`, null, null);

export const postBillingServiceCorrection = (
  contractId: string,
  correction: { patientId: string; serviceStart?: string; serviceEnd?: string; reason: string },
) => post(gdpService, `/billing/contracts/${contractId}/service-corrections`, correction, null);

export const deleteBillingServiceCorrection = (contractId: string, correctionId: string) =>
  del(gdpService, `/billing/contracts/${contractId}/service-corrections/${correctionId}`);

export const getBillingContractVersions = (contractId: string) =>
  get(gdpService, `/billing/contracts/${contractId}/versions`, null, null);

export const putBillingContractVersionPrice = (contractId: string, versionId: string, unitPrice: number | null) =>
  put(gdpService, `/billing/contracts/${contractId}/versions/${versionId}/price`, { unitPrice });

export const postBillingContractVersion = (
  contractId: string,
  fromYear: number,
  fromMonth: number,
  unitPrice: number,
) => post(gdpService, `/billing/contracts/${contractId}/versions`, { fromYear, fromMonth, unitPrice }, null);

export const getBillingSettlements = (contractId: string) =>
  get(gdpService, `/billing/contracts/${contractId}/settlements`, null, null);

export const postBillingSettlementDraft = (contractId: string, year: number, month: number) =>
  post(gdpService, `/billing/contracts/${contractId}/settlements`, {}, { year, month });

export const postBillingSettlementImport = (contractId: string, year: number, month: number, note: string) =>
  post(gdpService, `/billing/contracts/${contractId}/settlements/import`, { note }, { year, month });

export const getBillingSettlement = (settlementId: string) =>
  get(gdpService, `/billing/settlements/${settlementId}`, null, null);

export const getBillingSettlementChecks = (settlementId: string) =>
  get(gdpService, `/billing/settlements/${settlementId}/checks`, null, null);

export type BillingTransition = 'submit' | 'reject' | 'validate' | 'issue' | 'annul';

export const postBillingSettlementTransition = (
  settlementId: string,
  transition: BillingTransition,
  body: Record<string, string | undefined> = {},
) => post(gdpService, `/billing/settlements/${settlementId}/${transition}`, body, null);

export const getBillingSettlementRender = (settlementId: string, kind: 'detail' | 'summary') =>
  getBlob(gdpService, `/billing/settlements/${settlementId}/render/${kind}`);

export const postBillingSettlementDispatch = (settlementId: string, purpose: 'REVIEW' | 'INVOICING') =>
  post(gdpService, `/billing/settlements/${settlementId}/dispatch`, {}, { purpose });

export const getBillingSettlementDispatches = (settlementId: string) =>
  get(gdpService, `/billing/settlements/${settlementId}/dispatches`, null, null);

export const getBillingSettlementReviewLinks = (settlementId: string) =>
  get(gdpService, `/billing/settlements/${settlementId}/review-links`, null, null);

export const getBillingSettlementDocuments = (settlementId: string) =>
  get(gdpService, `/billing/settlements/${settlementId}/documents`, null, null);

export const getBillingSettlementDocument = (settlementId: string, documentId: string) =>
  getBlob(gdpService, `/billing/settlements/${settlementId}/documents/${documentId}`);

export const postBillingSettlementEvidence = (settlementId: string, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return postForm(gdpService, `/billing/settlements/${settlementId}/documents`, form);
};

export const getBillingClientRecipients = (clientId: string) =>
  get(gdpService, `/billing/clients/${clientId}/recipients`, null, null);

export const postBillingClientRecipient = (
  clientId: string,
  recipient: { purpose: 'REVIEW' | 'INVOICING'; name?: string; email: string },
) => post(gdpService, `/billing/clients/${clientId}/recipients`, recipient, null);

export const deleteBillingClientRecipient = (clientId: string, recipientId: string) =>
  del(gdpService, `/billing/clients/${clientId}/recipients/${recipientId}`);

// Página pública de revisión (sin sesión): la credencial es el enlace personal del correo, que va en la cabecera
// Review-Token y no en la ruta para que no quede en los registros de acceso del back.
const reviewHeaders = (token: string) => ({ 'Review-Token': token });

export const getBillingReview = (token: string) => get(gdpService, '/billing/review', null, null, reviewHeaders(token));

export const getBillingReviewDetail = (token: string) =>
  getBlob(gdpService, '/billing/review/detail', reviewHeaders(token));

export const postBillingReviewAction = (
  token: string,
  action: 'validate' | 'request-changes',
  body: { name: string; note?: string },
) => post(gdpService, `/billing/review/${action}`, body, null, reviewHeaders(token));
